import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Instagram, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const sized = (url) => (url.includes("images.unsplash.com") && !url.includes("&w=") ? `${url}&w=700` : url);
const srcOf = (p) => (p.src.startsWith("http") ? sized(p.src) : `${process.env.REACT_APP_BACKEND_URL}${p.src}`);

const InstagramStrip = () => {
  const [photos, setPhotos] = useState([]);
  const track = useRef(null);

  useEffect(() => {
    axios.get(`${API}/gallery`).then((r) => setPhotos(r.data)).catch(() => {});
  }, []);

  const scrollBy = (dir) => {
    const el = track.current;
    if (el) el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.8, 640), behavior: "smooth" });
  };

  if (!photos.length) return null;

  return (
    <section id="instagram" data-testid="instagram-section" className="py-20 lg:py-28 overflow-hidden">
      <motion.div
        initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.6 }}
        className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto flex flex-wrap items-end justify-between gap-6 mb-10"
      >
        <div>
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#0B1F3A] font-mono-alt font-medium mb-4">Instagram</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            <span className="gold-text">I nostri lavori</span>
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="https://instagram.com/bany.barber"
            target="_blank"
            rel="noreferrer"
            data-testid="instagram-profile-link"
            className="inline-flex items-center gap-2 rounded-full bg-[#0B1F3A] text-[#F7F2EC] px-5 py-3 text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#071528] transition-colors duration-300"
          >
            <Instagram className="w-4 h-4" strokeWidth={1.8} /> @bany.barber
          </a>
          <div className="hidden sm:flex gap-2">
            <button data-testid="instagram-prev" aria-label="Foto precedenti" onClick={() => scrollBy(-1)} className="rounded-full border border-[rgba(11,31,58,0.25)] p-3 hover:border-[#0B1F3A] hover:bg-white transition-colors">
              <ChevronLeft className="w-4 h-4 text-[#0B1F3A]" />
            </button>
            <button data-testid="instagram-next" aria-label="Foto successive" onClick={() => scrollBy(1)} className="rounded-full border border-[rgba(11,31,58,0.25)] p-3 hover:border-[#0B1F3A] hover:bg-white transition-colors">
              <ChevronRight className="w-4 h-4 text-[#0B1F3A]" />
            </button>
          </div>
        </div>
      </motion.div>

      <div
        ref={track}
        data-testid="instagram-track"
        className="flex gap-4 lg:gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-none px-4 sm:px-8 lg:px-16 pb-4"
        style={{ scrollbarWidth: "none" }}
      >
        {photos.map((p) => (
          <a
            key={p.id}
            href="https://instagram.com/bany.barber"
            target="_blank"
            rel="noreferrer"
            data-testid={`instagram-photo-${p.id}`}
            className="group relative shrink-0 snap-start w-[72vw] sm:w-[340px] lg:w-[380px] aspect-[4/5] overflow-hidden rounded-2xl bg-[#0B1F3A]/5"
          >
            <img
              src={srcOf(p)}
              alt={p.caption || "BANY Barbershop"}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <span className="absolute inset-0 bg-[#0B1F3A]/0 group-hover:bg-[#0B1F3A]/25 transition-colors duration-500" />
            <span className="absolute bottom-4 left-4 right-4 flex items-center gap-2 text-[#F7F2EC] text-xs uppercase tracking-[0.15em] opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <Instagram className="w-4 h-4" strokeWidth={1.8} /> {p.caption || "@bany.barber"}
            </span>
          </a>
        ))}
      </div>
    </section>
  );
};

export default InstagramStrip;
